import { EpisodicEpaFilingFieldsFragment, WasteEpisodicEventType } from "generated-graphql/graphql";
import { DateTime } from "luxon";

import { formatContainerDateLong } from "../Containers/containerDisplay";
import { EPISODIC_PROJECT_TYPE_LABELS } from "../Submissions/Stepper/rcraNotificationLabels";

export type EpaFilingKeyInput = { handlerId: string; activityLocation: string; seqNumber: number };

export const EPA_FILING_TYPE_MISMATCH_HINT = "Type doesn't match this event";
export const EPA_FILING_VERIFIED_LABEL = "Verified against EPA records";

const TYPE_LABELS: Record<WasteEpisodicEventType, string> = {
  [WasteEpisodicEventType.Planned]: "Planned",
  [WasteEpisodicEventType.Unplanned]: "Unplanned",
};

export function epaFilingKey(filing: EpisodicEpaFilingFieldsFragment): EpaFilingKeyInput {
  return {
    handlerId: filing.handlerId,
    activityLocation: filing.activityLocation,
    seqNumber: filing.seqNumber,
  };
}

/**
 * Whether two filings are the same EPA record. Both absent counts as the
 * same, so a save can tell "the link never changed" from "the link was
 * cleared" without comparing fields at each call site.
 */
export function sameEpaFilingKey(
  a: EpaFilingKeyInput | null | undefined,
  b: EpaFilingKeyInput | null | undefined
): boolean {
  if (a == null || b == null) {
    return a == null && b == null;
  }
  return (
    a.handlerId === b.handlerId &&
    a.activityLocation === b.activityLocation &&
    a.seqNumber === b.seqNumber
  );
}

/** "May 26, 2026" or "May 26 to June 10, 2026"; "Dates not reported" when EPA has none. */
function formatFilingWindow(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!start) {
    return "Dates not reported";
  }
  if (!end || end === start) {
    return formatContainerDateLong(start);
  }
  const startDate = DateTime.fromISO(start, { zone: "utc" });
  const endDate = DateTime.fromISO(end, { zone: "utc" });
  if (startDate.year === endDate.year) {
    return `${startDate.toFormat("LLLL d")} to ${endDate.toFormat("LLLL d, yyyy")}`;
  }
  return `${formatContainerDateLong(start)} to ${formatContainerDateLong(end)}`;
}

/**
 * The stepper's labels read "Unplanned — accidental spills"; the candidate
 * line already states the type, so the leading "Unplanned — "/"Planned — "
 * prefix (an em dash, not a hyphen) is stripped and the remainder capitalized.
 */
function reasonLabel(projectType: keyof typeof EPISODIC_PROJECT_TYPE_LABELS): string {
  const label = EPISODIC_PROJECT_TYPE_LABELS[projectType];
  const stripped = label.replace(/^(Planned|Unplanned)\s*[—–-]\s*/i, "");
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export function describeEpaFiling(filing: EpisodicEpaFilingFieldsFragment): string {
  const parts = [
    TYPE_LABELS[filing.eventType],
    formatFilingWindow(filing.startDate, filing.endDate),
  ];
  if (filing.projectTypes.length > 0) {
    parts.push(filing.projectTypes.map(reasonLabel).join(", "));
  }
  return parts.join(" · ");
}

export function describeEpaFilingReceipt(filing: EpisodicEpaFilingFieldsFragment): string {
  return `Received by EPA ${formatContainerDateLong(filing.receiveDate)} · seq ${filing.seqNumber}`;
}
